import React, { useContext } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Home, PanelLeftClose, PanelLeftOpen } from 'lucide-react';
import { NAV_ITEMS } from '../../lib/nav';
import { useFeatures } from '../../context/FeatureContext';
import { AppContext } from '../../context/AppContext';
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from '../ui/tooltip';
import { cn } from '../../lib/utils';

// Production desktop sidebar: collapsible icon-rail ↔ expanded, home identity in
// the header, feature-gated nav with active state + collapsed tooltips, and a
// clean footer collapse control. Replaces the hand-rolled BottomNavigation.
export default function AppSidebar({ collapsed, onToggle }) {
  const location = useLocation();
  const { isFeatureEnabled, loading } = useFeatures();
  const { config, isConnected } = useContext(AppContext);
  const homeName = config?.system?.name || 'Mirfa Home';

  const items = loading
    ? []
    : NAV_ITEMS.filter((item) => !item.feature || isFeatureEnabled(item.feature));

  const isActive = (path) => location.pathname.startsWith(path);

  return (
    <TooltipProvider delayDuration={0}>
      <aside
        className={cn(
          'hidden md:flex fixed inset-y-0 left-0 z-40 flex-col border-r border-sidebar-border bg-sidebar/95 backdrop-blur-xl transition-[width] duration-300 ease-in-out',
          collapsed ? 'w-[76px]' : 'w-64'
        )}
      >
        {/* Header — home identity */}
        <div className={cn('flex h-16 items-center gap-3 border-b border-sidebar-border px-4', collapsed && 'justify-center px-0')}>
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-primary text-primary-foreground shadow-sm">
            <Home className="h-5 w-5" strokeWidth={2} />
          </div>
          {!collapsed && (
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-semibold text-sidebar-foreground">{homeName}</p>
              <p className="truncate text-xs text-muted-foreground">
                {isConnected ? 'Online • Local Control' : 'Reconnecting…'}
              </p>
            </div>
          )}
        </div>

        {/* Nav */}
        <nav className="flex-1 space-y-1 overflow-y-auto p-3">
          {items.map((item) => {
            const Icon = item.icon;
            const active = isActive(item.path);
            const badge = item.id === 'settings' && !isConnected;
            const link = (
              <Link
                to={item.path}
                aria-current={active ? 'page' : undefined}
                className={cn(
                  'group relative flex items-center rounded-xl text-sm font-medium transition-colors',
                  collapsed ? 'mx-auto h-11 w-11 justify-center' : 'gap-3 px-3 py-2.5',
                  active
                    ? 'bg-sidebar-primary text-sidebar-primary-foreground shadow-sm'
                    : 'text-sidebar-foreground/70 hover:bg-sidebar-accent hover:text-sidebar-foreground'
                )}
              >
                <span className="relative">
                  <Icon className="h-5 w-5 shrink-0" strokeWidth={2} />
                  {badge && (
                    <span className="absolute -right-0.5 -top-0.5 h-2 w-2 rounded-full bg-destructive ring-2 ring-sidebar" />
                  )}
                </span>
                {!collapsed && <span className="truncate">{item.label}</span>}
              </Link>
            );

            return collapsed ? (
              <Tooltip key={item.id}>
                <TooltipTrigger asChild>{link}</TooltipTrigger>
                <TooltipContent side="right">{item.label}</TooltipContent>
              </Tooltip>
            ) : (
              <React.Fragment key={item.id}>{link}</React.Fragment>
            );
          })}
        </nav>

        {/* Footer — collapse toggle */}
        <div className="border-t border-sidebar-border p-3">
          <button
            type="button"
            onClick={onToggle}
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            className={cn(
              'flex items-center rounded-xl text-sm font-medium text-sidebar-foreground/70 transition-colors hover:bg-sidebar-accent hover:text-sidebar-foreground',
              collapsed ? 'mx-auto h-11 w-11 justify-center' : 'w-full gap-3 px-3 py-2.5'
            )}
          >
            {collapsed ? (
              <PanelLeftOpen className="h-5 w-5" strokeWidth={2} />
            ) : (
              <>
                <PanelLeftClose className="h-5 w-5" strokeWidth={2} />
                <span>Collapse</span>
              </>
            )}
          </button>
        </div>
      </aside>
    </TooltipProvider>
  );
}
